import { useState } from 'react';
import { HeatmapEntry } from '../../lib/api/intelligence';

interface RejectionHeatmapProps {
  entries: HeatmapEntry[];
  loading?: boolean;
  onFilterChange?: (filters: { form_type?: string; state?: string }) => void;
}

function rateColor(rate: number): string {
  if (rate > 0.25) return 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300';
  if (rate >= 0.1) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300';
  return 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300';
}

export default function RejectionHeatmap({ entries, loading, onFilterChange }: RejectionHeatmapProps) {
  const [formType, setFormType] = useState('');
  const [state, setState] = useState('');

  const handleFilter = () => {
    onFilterChange?.({ form_type: formType || undefined, state: state || undefined });
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      <div className="border-b border-gray-200 dark:border-white/[0.05] px-6 py-4">
        <h3 className="text-base font-semibold text-gray-800 dark:text-white/90 mb-3">
          Rejection Heatmap by District
        </h3>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            placeholder="Form type (e.g. W-3A)"
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-1.5 text-sm text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
          />
          <input
            type="text"
            placeholder="State (e.g. TX)"
            value={state}
            onChange={(e) => setState(e.target.value)}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-1.5 text-sm text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 w-28"
          />
          <button
            onClick={handleFilter}
            className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
          >
            Filter
          </button>
        </div>
      </div>

      <div className="p-6">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
          </div>
        )}

        {!loading && entries.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
            No heatmap data available.
          </p>
        )}

        {!loading && entries.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    District
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    County
                  </th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Rejections
                  </th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Total Filings
                  </th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Rejection Rate
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {entries.map((entry, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                    <td className="py-2 px-3 font-medium text-gray-800 dark:text-white/90">
                      {entry.district}
                    </td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                      {entry.county || '—'}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-800 dark:text-white/90">
                      {entry.rejection_count}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-400">
                      {entry.total_filings}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${rateColor(
                          entry.rejection_rate
                        )}`}
                      >
                        {Math.round(entry.rejection_rate * 100)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-green-200" /> &lt;10%
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-yellow-200" /> 10–25%
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-red-200" /> &gt;25%
          </span>
        </div>
      </div>
    </div>
  );
}
